import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useApiClient } from '../providers/auth-provider';

import type { TenantUserResponse } from '../lib/api-client';
import type { CreateUserBody, SortOption, UpdateUserBody, UserData, UserRole, UserStatus } from '../lib/types';

export interface UserFilters {
  role: UserRole | null;
  status: UserStatus;
  search: string;
  sortBy: SortOption;
}

function mapResponse(r: TenantUserResponse): UserData {
  return {
    object: 'user',
    id: r.id,
    email: r.email,
    display_name: r.display_name,
    role: r.role,
    tenant_id: r.tenant_id,
    deactivated_at: r.deactivated_at,
    deactivated_by: r.deactivated_by,
    metadata: r.metadata,
    created_at: r.created_at,
    updated_at: r.updated_at,
  };
}

export function useUsers({ enabled = false, pollInterval = 30000 }: { enabled?: boolean; pollInterval?: number } = {}) {
  const apiClient = useApiClient();
  const [users, setUsers] = useState<UserData[]>([]);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [filters, setFiltersState] = useState<UserFilters>({ role: null, status: 'active', search: '', sortBy: 'newest' });
  const filtersRef = useRef(filters);

  const hasLoadedOnce = useRef(false);
  const fetchInFlightRef = useRef(false);

  const doFetch = useCallback(() => {
    if (fetchInFlightRef.current) return;
    fetchInFlightRef.current = true;
    const f = filtersRef.current;
    const query: Record<string, string> = {};
    if (f.status) query.status = f.status;
    if (f.role) query.role = f.role;
    apiClient
      .listUsers(Object.keys(query).length > 0 ? query : undefined)
      .then((res) => {
        setUsers(res.data.map(mapResponse));
        hasLoadedOnce.current = true;
        setStatus('success');
        setIsRefreshing(false);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Failed to load');
        if (!hasLoadedOnce.current) setStatus('error');
        setIsRefreshing(false);
      })
      .finally(() => { fetchInFlightRef.current = false; });
  }, [apiClient]);

  const doFetchRef = useRef(doFetch);
  doFetchRef.current = doFetch;

  // Initial fetch
  useEffect(() => {
    if (!enabled) return;
    setStatus('loading');
    doFetchRef.current();
  }, [enabled]);

  // Polling
  useEffect(() => {
    if (!enabled) return;
    const interval = setInterval(() => {
      if (fetchInFlightRef.current) return;
      if (typeof document !== 'undefined' && document.hidden) return;
      setIsRefreshing(true);
      doFetchRef.current();
    }, pollInterval);
    return () => clearInterval(interval);
  }, [enabled, pollInterval]);

  const setFilters = useCallback((f: UserFilters) => {
    filtersRef.current = f;
    setFiltersState(f);
    if (hasLoadedOnce.current) {
      setIsRefreshing(true);
      fetchInFlightRef.current = false;
      doFetchRef.current();
    }
  }, []);

  const reload = useCallback(() => {
    if (hasLoadedOnce.current) {
      setIsRefreshing(true);
      fetchInFlightRef.current = false;
      doFetchRef.current();
    }
  }, []);

  // Client-side filtering (search + sort)
  const filtered = useMemo(() => {
    let result = [...users];
    const f = filters;

    if (f.search) {
      const q = f.search.toLowerCase();
      result = result.filter(
        (u) =>
          u.email.toLowerCase().includes(q) ||
          (u.display_name?.toLowerCase().includes(q)),
      );
    }

    if (f.sortBy === 'oldest') {
      result.sort((a, b) => a.created_at.localeCompare(b.created_at));
    } else {
      result.sort((a, b) => b.created_at.localeCompare(a.created_at));
    }

    return result;
  }, [users, filters]);

  const roleCounts = useMemo(() => {
    const c: Record<UserRole, number> = { owner: 0, admin: 0, member: 0 };
    for (const u of users) { c[u.role] = (c[u.role] ?? 0) + 1; }
    return c;
  }, [users]);

  // CRUD operations
  const createUser = useCallback(async (body: CreateUserBody) => {
    const created = await apiClient.createUser(body);
    const mapped = mapResponse(created);
    setUsers((prev) => [mapped, ...prev]);
    return mapped;
  }, [apiClient]);

  const updateUser = useCallback(async (id: string, body: UpdateUserBody) => {
    const updated = await apiClient.updateUser(id, body);
    const mapped = mapResponse(updated);
    setUsers((prev) => prev.map((u) => u.id === id ? mapped : u));
    return mapped;
  }, [apiClient]);

  const deactivateUser = useCallback(async (id: string) => {
    const deactivated = await apiClient.deactivateUser(id);
    const mapped = mapResponse(deactivated);
    // Remove from active list since they're now deactivated
    setUsers((prev) => prev.filter((u) => u.id !== id));
    return mapped;
  }, [apiClient]);

  return {
    users: filtered,
    allUsers: users,
    status,
    isRefreshing,
    error,
    roleCounts,
    reload,
    filters,
    setFilters,
    createUser,
    updateUser,
    deactivateUser,
  };
}
